import { Component, DestroyRef, inject, LOCALE_ID, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { take } from 'rxjs';

import { LanguagePreferenceService } from '../../services/language-preference.service';
import { AppProfileService } from '../../services/profile.service';

@Component({
    imports: [CommonModule, RouterModule],
    selector: 'app-language-menu',
    templateUrl: './language-menu.component.html',
    styleUrls: ['./language-menu.component.css']
})
export class LanguageMenuComponent implements OnInit {
  private languagePreference = inject(LanguagePreferenceService);
  private profileService = inject(AppProfileService);
  activeLocale = inject(LOCALE_ID);

  locales = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
  ];

  /** The id the preference is stored against, or null while nobody is signed in. */
  private profileId: string | null = null;
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    // LOCALE_ID is the locale this bundle actually renders in, and since #178 it is also what
    // every per-request value is derived from — so the menu's label describes the page the user
    // is looking at, not a preference that may describe a different one. `bundleLanguage` folds
    // the formatting locale (`en-GB` since #180) back to the bare tag the menu offers.
    this.activeLocale = this.languagePreference.bundleLanguage;

    // Switching writes the profile-scoped setting when there is a profile; an anonymous visitor
    // just changes bundle. Subscribed rather than taken once: the menu outlives sign-in, and the
    // first profile can arrive long after the nav rendered.
    this.profileService.currentProfile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((profile) => (this.profileId = profile?.id ?? null));
  }

  public setLanguage(lang: string): void {
    // The service owns the order: write the preference, then switch the page — and switch it
    // either way, because a preference that failed to save is no reason to refuse the language
    // the user just asked to read in.
    this.languagePreference.apply(lang, this.profileId).pipe(take(1)).subscribe();
  }
}
